import React, { FC, useEffect, useState } from "react";
import Autocomplete from "../../ui/Autocomplete";
import Input from "../../ui/Input";
import { Editor, OnValidate } from "@monaco-editor/react";
import Button from "../../ui/Button";
import { FaInfoCircle, FaPlusCircle, FaSave } from "react-icons/fa";
import Modal from "../../ui/Modal";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { ActionPlace, IAction, IFlow } from "../../../types/ApiTypes";
import useApi from "../../../hooks/useApi";
import Popover from "../../ui/Popover";
import Card from "../../ui/Card";

interface Props {
  isOpen?: boolean;
  action?: IAction;
  flowsList: { label: string, value: IFlow }[];
  onActionCreated: (action: IAction) => void;
  onActionEdited: (action: IAction) => void;
  onClose?: () => void;
}

interface InputValues {
  name: string;
  flow: number;
  extraPayload: string;
}

const ActionModal: FC<Props> = (props) => {
  const { handleSubmit, register, reset, setValue, control, formState: { errors } } = useForm<InputValues>();
  const { createAction, editAction } = useApi();

  const [isOpen, setIsOpen] = useState(false);
  const [flowPayloadErrors, setFlowPayloadErrors] = useState<string[]>([]);

  useEffect(() => {
    setIsOpen(props.isOpen || false);
    if (props.action) {
      setValue("name", props.action.name);
      setValue("flow", props.action.flow.id);
      setValue("extraPayload", props.action.extraPayload || "");
    } else {
      reset();
    }
  }, [props.isOpen, props.action]);

  const onValidate: OnValidate = (markers) => {
    const messages: string[] = markers.map(el => el.message);
    setFlowPayloadErrors(messages);
  };

  const onSubmit: SubmitHandler<InputValues> = async (values) => {
    if (flowPayloadErrors.length) return;
    const id = toast.loading("Die Erstellung läuft");
    try {
      const payload = {
        name: values.name,
        flow: values.flow,
        place: ActionPlace.Details,
        extraPayload: values.extraPayload || undefined
      };
      const action = await (props.action ? editAction({ ...payload, id: props.action.id }) : createAction(payload))
      if (action) {
        props.action ? props.onActionEdited(action) : props.onActionCreated(action);
      }
      setIsOpen(false);
      reset();
      if (props.onClose) props.onClose();
      return toast.success(<span className="text-sm">Die Action wurde {props.action ? 'bearbeitet' : 'erstellt'}</span>, { id });
    } catch (err: any) {
      return toast.error(<span className="text-sm">Die Action konnte nicht {props.action ? 'bearbeitet' : 'erstellt'} werden</span>, { id });
    }
  };

  const onClose = () => {
    setIsOpen(false);
    reset();
    if (props.onClose) props.onClose();
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}><FaPlusCircle className="mr-2" /> Neue Action
        erstellen</Button>
      <Modal isOpen={isOpen} onClose={onClose} title="Neue Action erstellen">
        <form className="space-y-2" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="flow_url" className="font-semibold">Flow</label>
            <Controller name="flow" rules={{ required: "Flow ist ein Pflichtfeld" }} control={control}
                        render={({ field, fieldState }) => (
                          <Autocomplete options={props.flowsList} placeholder="Wähle ein Flow"
                                        defaultValue={props.flowsList.find(x => field.value === x.value.id)}
                                        onChange={(e) => {console.log(e); field.onChange(e.value.id)}} />
                        )} />
            {errors.flow && <span className="bg-error-brighter text-sm">{errors.flow.message}</span>}
          </div>
          <div>
            <label htmlFor="name" className="font-semibold">Name</label>
            <Input id="name" label="Flow name" {...register("name", { required: true, minLength: 5 })} />
            <small>Der Name wird in dem Action-Fenster zu sehen sein</small>
            {errors.name &&
              <span className="bg-error-brighter text-sm">Der Name ist ein Pflichtfeld und muss mindestens 5 Zeichen lang sein.</span>}
          </div>
          <div>
            <label htmlFor="flow_name" className="font-semibold flex items-center gap-2">
              <span>Payload</span>
              <Popover trigger={<span><FaInfoCircle/></span>}>
                <Card className='w-full'>
                  <ul className='text-sm font-normal'>
                    <li><b>{`{{SPACE_TYPE}}`}</b> - Typ des Spaces (20, 25, etc.)</li>
                    <li><b>{`{{SPACE_NAME}}`} </b> - Name des Spaces</li>
                    <li><b>{`{{SPACE_DESCRIPTION}}`}</b> - Beschreibung des Spaces</li>
                    <li><b>{`{{SPACE_PRIMARY_OWNER_ACC_ID}}`}</b> - Azure ID des Besitzers</li>
                    <li><b>{`{{SPACE_SECONDARY_OWNER_ACC_ID}}`}</b> - Azure ID des Stellvertreters</li>
                  </ul>
                </Card>
              </Popover>
            </label>
            <Controller name="extraPayload" control={control} render={({ field, fieldState }) => (
              <div className="overflow-hidden rounded-md">
                <Editor height="14rem" defaultLanguage="json" defaultValue="" theme="vs-dark"
                        onChange={(e) => field.onChange(e)} value={field.value} onValidate={onValidate} />
              </div>
            )} />
            {!flowPayloadErrors.length &&
              <small>Extra payload in JSON-Format welches an das Flow weitergegeben wird</small>}
            {flowPayloadErrors.length > 0 && (
              <div className="flex flex-col gap-1">
                {flowPayloadErrors.map(el => (
                  <small key={el} className="text-red-500">{el}</small>
                ))}
              </div>
            )}
          </div>
          <Button type="submit">
            {props.action ? <span className="flex gap-2 items-center"><FaSave />Speichern</span> :
              <span className="flex gap-2 items-center"><FaPlusCircle />Erstellen</span>}
          </Button>
        </form>
      </Modal>
    </>
  );
};

export default ActionModal;